import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  MapPin,
  Users,
  Medal,
  CalendarDays,
  MapPinned,
  Trophy,
  Image as ImageIcon,
  ListChecks,
  Calendar,
} from "lucide-react";
import { apiFetch } from "../../services/api/Events";
import PageHeader from "../Header";

interface EventItem {
  title: string;
  date: string;
  location: string;
  athletes: string;
  description: string;
  image: string;
  sport_link?: string | null;
  schedule_link?: string | null;
  venue_link?: string | null;
  result_link?: string | null;
  medal_tally_link?: string | null;
  match_fixtures_link?: string | null;
  media_gallery_link?: string | null;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  links: { url: string | null; label: string; active: boolean }[];
}

export function UpdatePage() {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [overflowing, setOverflowing] = useState<Record<number, boolean>>({});
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const descRefs = useRef<(HTMLDivElement | null)[]>([]);

  const toggleExpand = (index: number) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const quickActions = [
    { name: "Sports", key: "sport_link", icon: <Medal className="h-6 w-6" /> },
    {
      name: "Schedule",
      key: "schedule_link",
      icon: <CalendarDays className="h-6 w-6" />,
    },
    {
      name: "Venues",
      key: "venue_link",
      icon: <MapPinned className="h-6 w-6" />,
    },
    {
      name: "Results",
      key: "result_link",
      icon: <Trophy className="h-6 w-6" />,
    },
    {
      name: "Medal Tally",
      key: "medal_tally_link",
      icon: <ListChecks className="h-6 w-6" />,
    },
    {
      name: "Match Fixtures",
      key: "match_fixtures_link",
      icon: <CalendarDays className="h-6 w-6" />,
    },
    {
      name: "Media & Gallery",
      key: "media_gallery_link",
      icon: <ImageIcon className="h-6 w-6" />,
    },
  ];

  // ✅ Fetch events with pagination
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiFetch(`view-all-events?page=${page}`);
        console.log("API Response:", response?.data);

        const formattedEvents: EventItem[] = (response?.data?.data || []).map(
          (item: any) => ({
            title: item.title,
            date: item.event_date,
            location: item.location,
            athletes: `Expected athlete ${item.registered_count}+`,
            description: item.description,
            image: item.image_url || "/images/logo approved.png",
            sport_link: item.sport_link,
            schedule_link: item.schedule_link,
            venue_link: item.venue_link,
            result_link: item.result_link,
            medal_tally_link: item.medal_tally_link,
            match_fixtures_link: item.match_fixtures_link,
            media_gallery_link: item.media_gallery_link,
          }),
        );

        setEvents(formattedEvents);
        setMeta(response?.data?.meta || null);
      } catch (error) {
        console.error("Error fetching events:", error);
        setError("Failed to load events. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page]);

  const handlePageChange = (newPage: number) => {
    if (meta && newPage >= 1 && newPage <= meta.last_page) {
      setPage(newPage);
    }
  };

  // Detect description overflow
  useEffect(() => {
    const newOverflowing: Record<number, boolean> = {};
    descRefs.current.forEach((ref, index) => {
      if (ref) {
        const lineHeight = parseFloat(getComputedStyle(ref).lineHeight) || 16;
        const maxHeight = lineHeight * 3;
        newOverflowing[index] = ref.scrollHeight > maxHeight + 2;
      }
    });
    setOverflowing(newOverflowing);
  }, [events]);

  return (
    <section>
      <div className="max-w-full ">
        <PageHeader
          title="Updates & Notifications"
          paragraph="Explore Haryana’s premier sports events, latest updates, and important notifications."
        />

        {/* Loader */}
        {loading && (
          <div className="text-center text-gray-500 py-10 animate-pulse">
            Loading events...
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="text-center text-red-500 py-10">{error}</div>
        )}

        {/* Empty */}
        {!loading && !error && events.length === 0 && (
          <div className="text-center text-gray-500 py-10">
            No upcoming events found.
          </div>
        )}

        {/* Event Grid */}
        {!loading && !error && events.length > 0 && (
          <div
            className={`max-w-6xl mx-auto py-10 px-5 grid gap-6 md:gap-8 ${
              events.length === 1
                ? "place-items-center grid-cols-1"
                : "grid-cols-1 md:grid-cols-2"
            }`}
          >
            {events.map((event, index) => (
              <Card
                key={event.title}
                className="overflow-hidden rounded-3xl border border-gray-200 bg-white/90 backdrop-blur-xl shadow-lg hover:shadow-2xl transition-all duration-500 group"
              >
                <div className="grid lg:grid-cols-[420px_1fr]">
                  {/* Left Side Image */}
                  <div className="relative overflow-hidden bg-gradient-to-br from-orange-500 via-orange-400 to-yellow-400 p-6 flex items-center justify-center">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,white,transparent_40%)]" />

                    <div className="relative z-10">
                      <div className="w-60 h-60 rounded-full bg-white/20 backdrop-blur-md border border-white/30 p-5 shadow-2xl flex items-center justify-center group-hover:scale-105 transition duration-500">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-full object-contain drop-shadow-xl"
                        />
                      </div>
                    </div>

                    {/* Floating Badge */}
                    <div className="absolute top-5 left-5 bg-white/20 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-xs font-semibold border border-white/30">
                      Upcoming Event
                    </div>
                  </div>

                  {/* Right Side Content */}
                  <CardContent className="p-5 md:p-8 flex flex-col justify-between">
                    {/* Title */}
                    <div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                        {event.title}
                      </h3>

                      <p className="text-sm text-gray-500 mt-1">
                        Haryana Olympics Association
                      </p>

                      {/* Event Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                        {/* Date */}
                        <div className="flex items-start gap-3 p-3 rounded-2xl bg-orange-50 border border-orange-100">
                          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-white shadow-sm">
                            <Calendar className="h-5 w-5 text-orange-500" />
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">
                              {event.date || "Coming Soon"}
                            </h4>
                            <p className="text-xs text-gray-500 mt-0.5">
                              November 2026
                            </p>
                          </div>
                        </div>

                        {/* Location */}
                        <div className="flex items-start gap-3 p-3 rounded-2xl bg-orange-50 border border-orange-100">
                          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-white shadow-sm">
                            <MapPin className="h-5 w-5 text-orange-500" />
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">
                              {event.location}
                            </h4>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Multiple Venues
                            </p>
                          </div>
                        </div>

                        {/* Athletes */}
                        <div className="flex items-start gap-3 p-3 rounded-2xl bg-green-50 border border-green-100">
                          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-white shadow-sm">
                            <Users className="h-5 w-5 text-green-500" />
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-gray-800">
                              {event.athletes}
                            </h4>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <div
                        ref={(el) => {
                          descRefs.current[index] = el;
                        }}
                        className={`
    mt-6
    text-gray-600
    text-lg md:text-base
    leading-relaxed
    overflow-hidden
    ${expanded[index] ? "" : "line-clamp-3"}
  `}
                        dangerouslySetInnerHTML={{
                          __html: event.description,
                        }}
                      />

                      {overflowing[index] && (
                        <button
                          onClick={() => toggleExpand(index)}
                          className="text-orange-500 text-sm mt-2 hover:underline"
                        >
                          {expanded[index] ? "Show Less" : "Read More"}
                        </button>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-wrap gap-3 mt-8">
                      <Button className="rounded-xl bg-orange-500 hover:bg-orange-600 px-6 py-5 text-sm font-medium shadow-lg shadow-orange-200">
                        Learn More
                      </Button>

                      <Button
                        onClick={() => {
                          document
                            .getElementById("newsletter")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        }}
                        variant="outline"
                        className="rounded-xl border-orange-200 text-orange-500 hover:bg-orange-50 px-6 py-5 text-sm font-medium"
                      >
                        Follow Event
                      </Button>
                    </div>
                  </CardContent>
                </div>
                {/* Quick Actions */}
                {/* <div className=" border-t border-gray-100 pt-6">
        <h4 className="text-xl text-center font-semibold text-gray-800 mb-4">
          Quick Actions
        </h4>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 p-3">
          {quickActions.map((item) => {
            const link = event[item.key as keyof EventItem] as
              | string
              | null;

            const isActive = Boolean(link);

            return (
              <a
                key={item.name}
                href={isActive ? link! : undefined}
                rel="noopener noreferrer"
                className="group/action rounded-2xl border border-gray-200 bg-white hover:bg-orange-50 hover:border-orange-200 transition-all duration-300 p-3 flex flex-col items-center justify-center shadow-sm hover:shadow-md"
              >
<div className="mb-2 text-orange-500 group-hover/action:scale-110 transition-transform flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-100 [&_svg]:w-7 [&_svg]:h-7">
  {item.icon}
</div>

                <span className="text-lg font-medium text-center text-gray-700 leading-tight">
                  {item.name}
                </span>
              </a>
            );
          })}
        </div>
      </div> */}
              </Card>
            ))}
          </div>
        )}

        {/* ✅ Pagination Controls */}
        {meta && meta.last_page > 1 && (
          <div className="flex flex-wrap justify-center items-center gap-1 sm:gap-2 mt-10">
            <Button
              variant="outline"
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-full"
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
            >
              Previous
            </Button>

            {/* Generate dynamic pagination with dots */}
            {Array.from({ length: meta.last_page }, (_, i) => i + 1)
              .filter((num) => {
                return (
                  num === 1 ||
                  num === meta.last_page ||
                  (num >= page - 1 && num <= page + 1)
                );
              })
              .reduce((acc: (number | string)[], curr, idx, arr) => {
                if (idx > 0 && arr[idx - 1] !== curr - 1) acc.push("...");
                acc.push(curr);
                return acc;
              }, [])
              .map((num, idx) =>
                num === "..." ? (
                  <span
                    key={idx}
                    className="px-3 py-1 text-gray-400 select-none text-sm sm:text-base"
                  >
                    ...
                  </span>
                ) : (
                  <Button
                    key={idx}
                    variant={num === page ? "default" : "outline"}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg transition-all duration-300 ${
                      num === page
                        ? "bg-orange-600 text-white shadow-md"
                        : "text-orange-600 hover:bg-orange-100"
                    }`}
                    onClick={() => {
                      if (typeof num === "number") handlePageChange(num);
                    }}
                  >
                    {num}
                  </Button>
                ),
              )}

            <Button
              variant="outline"
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-full"
              disabled={page === meta.last_page}
              onClick={() => handlePageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
